import { Row, Col, Card, Typography } from "antd";
import {
  useEffect,
  useState,
  useCallback,
  lazy,
  Suspense,
  useMemo,
} from "react";
import { ScanOutlined } from "@ant-design/icons";
import GETConsultarStock from "../../helpers/consultar-stock/GETConsultarStock";
import AppLayout from "../../components/layout/AppLayout";
import HeaderBack from "../../components/layout/headers/HeaderBack";
import Loader from "../../components/loader/Loader";
import { isEmptyArray } from "../../utils/isEmptyArray";
import { getCardStyle } from "../../utils/getCardStyle";
import { formatEtiquetaUbicacion } from "../../utils/formatEtiquetaUbicacion";
import ArticuloCardConsultaStock from "./cardsRender/ArticuloCardConsultaStock";
import PaletCardConsultaStock from "./cardsRender/PaletCardConsultaStock";
const { Text } = Typography;

const ModalTecladoAlfanumerico = lazy(
  () =>
    import("../../components/modalTecladoAlfanumerico/ModalTecladoAlfanumerico"),
);

const ConsultarStock = ({ darkMode, setDarkMode }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [buffer, setBuffer] = useState("");
  const [tecladoVisible, setTecladoVisible] = useState(false);
  const [valorTipeado, setValorTipeado] = useState("");
  const [tipoCodRes, setTipoCodRes] = useState(null);

  // Agrega esta función utilitaria dentro del componente o fuera (recomendado fuera)
  const esItemValido = (item) => {
    if (!item || typeof item !== "object") return false;

    // Caso Artículo: requiere idartl + codigo
    const esArticuloValido =
      typeof item.idartl === "string" &&
      item.idartl.trim() !== "" &&
      typeof item.codigo === "string" &&
      item.codigo.trim() !== "";

    // Caso Palet: requiere al menos idpale o codplt
    const esPaletValido =
      (typeof item.idpale === "string" && item.idpale.trim() !== "") ||
      (typeof item.codplt === "string" && item.codplt.trim() !== "");

    return esArticuloValido || esPaletValido;
  };

  const abrirTeclado = () => {
    setTecladoVisible(true);
  };

  const manejarConfirmacionTeclado = (valor) => {
    setTecladoVisible(false);
    handleSearch(valor);
  };

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) return;

    // Mantengo en memoria el valor tipeado para mostrar en caso de busqueda sin resultados.
    setValorTipeado(query.trim().toUpperCase());

    setHasSearched(true);
    setLoading(true);
    setItems([]);

    try {
      const response = await GETConsultarStock(query.trim());

      const DESCRIPCION_TIPO = {
        2: "Contenido del palet",
        0: "Stock del artículo",
        1: "Stock en la ubicación",
      };

      setTipoCodRes(DESCRIPCION_TIPO[response?.data?.[0]?.codres] ?? null);

      let datos = [];

      if (Array.isArray(response?.data)) {
        datos = response.data;
      }

      // Filtrar ítems vacíos antes de guardar en estado
      const itemsValidos = datos.filter(esItemValido);
      setItems(itemsValidos);
    } catch (error) {
      console.error("Error al cargar stock:", error);
      setItems([]);
    } finally {
      setLoading(false);
      setBuffer("");
    }
  }, []);

  // Manejador de teclas
  const handleKeyDown = useCallback(
    (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      ) {
        return;
      }

      const { key } = event;

      if (key === "Enter") {
        if (buffer.trim()) {
          handleSearch(buffer);
        }
      } else if (key.length === 1) {
        setBuffer((prev) => prev + key);
      }
    },
    [buffer, handleSearch],
  );

  // Determinar si todos los items pertenecen al mismo idartl
  const todosMismoArticulo = useMemo(() => {
    return (
      items.length > 0 && items.every((item) => item.idartl === items[0].idartl)
    );
  }, [items]);

  const totales = useMemo(() => {
    if (!todosMismoArticulo) return null;
    return items.reduce(
      (acc, item) => {
        acc.ctdcmp += item.ctdcmp || 0;
        acc.ctdret += item.ctdret || 0;
        acc.ctdpik += item.ctdpik || 0;
        acc.ctddis += item.ctddis || 0;
        acc.ctdplt += item.ctdplt || 0;
        acc.ctdfis += item.ctdfis || 0;
        return acc;
      },
      { ctdcmp: 0, ctdret: 0, ctdpik: 0, ctddis: 0, ctdplt: 0, ctdfis: 0 },
    );
  }, [items, todosMismoArticulo]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const headerComponent = (
    <HeaderBack
      modulo={"Consultar Stock"}
      data={{ headerTitle: "Consultar Stock" }}
      acciones={
        <span
          onClick={abrirTeclado}
          style={{ cursor: "pointer", display: "flex" }}
        >
          <ScanOutlined style={{ fontSize: "36px", color: "white" }} />
        </span>
      }
    />
  );

  return (
    <AppLayout
      darkMode={darkMode}
      setDarkMode={setDarkMode}
      header={headerComponent}
    >
      <div
        style={{
          height: "100%",
          padding: "8px",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {!hasSearched && !loading && (
          <Row gutter={[8, 8]} justify="center">
            <Col
              span={24}
              style={{
                position: "sticky",
                top: -10,
                zIndex: 10,
                padding: 0,
                backgroundColor: darkMode ? "#222" : "#fafafa",
                borderRadius: 6,
              }}
            >
              <div className="ubi-wrapper">
                <Typography.Title level={4} className="ubi-text">
                  Ingresa ubicación o código de artículo
                </Typography.Title>
              </div>
            </Col>
          </Row>
        )}

        {loading && <Loader />}

        {hasSearched && !loading && (
          <>
            {/* Título contextual */}
            {tipoCodRes && (
              <div style={{ textAlign: "center", marginBottom: "8px" }}>
                <Text
                  type="secondary"
                  style={{
                    fontSize: "17px",
                    fontWeight: "bold",
                    color: darkMode ? "#aaa" : "#555",
                    display: "inline-block",
                    padding: "4px 12px",
                    borderRadius: "4px",
                    width: "fit-content",
                    margin: "0 auto",
                  }}
                >
                  {tipoCodRes} {formatEtiquetaUbicacion(valorTipeado)}
                </Text>
              </div>
            )}

            <Row gutter={[8, 8]} justify="start" align="stretch">
              {!isEmptyArray(items) ? (
                items.map((item, index) => {
                  return item.codplt || item.idpale ? (
                    <PaletCardConsultaStock
                      darkMode={darkMode}
                      key={item.idartp + index}
                      item={item}
                    />
                  ) : (
                    <ArticuloCardConsultaStock
                      darkMode={darkMode}
                      key={item.idartp + index}
                      item={item}
                    />
                  );
                })
              ) : (
                <Col span={24}>
                  <Card
                    style={{
                      textAlign: "center",
                      backgroundColor: darkMode ? "#1f1f1f" : "#fafafa",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "32px",
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: "20px" }}>
                      No se encontraron resultados para{" "}
                      <strong>{formatEtiquetaUbicacion(valorTipeado)}</strong>
                    </Text>
                  </Card>
                </Col>
              )}
            </Row>
          </>
        )}

        {hasSearched && todosMismoArticulo && totales && (
          <Row gutter={[8, 8]} justify="center">
            <Col span={24}>
              <Card
                style={{
                  ...getCardStyle(darkMode),
                  backgroundColor: darkMode ? "#303030" : "#f0f2f5",
                  textAlign: "center",
                  marginTop: "8px",
                }}
              >
                <Typography.Title
                  level={5}
                  style={{ margin: 0, color: darkMode ? "#fff" : "#000" }}
                >
                  Totales del artículo
                </Typography.Title>
                <Row gutter={[8, 8]} style={{ marginTop: "12px" }}>
                  {[
                    { label: "cmp", value: Math.round(totales.ctdcmp) },
                    { label: "ret", value: Math.round(totales.ctdret) },
                    { label: "pik", value: Math.round(totales.ctdpik) },
                    { label: "dis", value: Math.round(totales.ctddis) },
                    { label: "plt", value: Math.round(totales.ctdplt) },
                    { label: "fis", value: Math.round(totales.ctdfis) },
                  ].map((stat) => (
                    <Col key={stat.label} xs={8} sm={4} md={4}>
                      <Text
                        strong
                        style={{
                          fontSize: "19px",
                          color: darkMode ? "#ddd" : "#333",
                        }}
                      >
                        {stat.label}: {stat.value}
                      </Text>
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          </Row>
        )}
      </div>

      {tecladoVisible && (
        <Suspense fallback={null}>
          <ModalTecladoAlfanumerico
            open={tecladoVisible}
            onClose={() => setTecladoVisible(false)}
            darkMode={darkMode}
            letras={["A", "B", "C", "J", "P", "#"]} // OTERO
            // letras={["A", "B", "C", "D", "P", "#"]} // OTROS
            onConfirm={manejarConfirmacionTeclado}
            title="Ingrese ubicación o código"
          />
        </Suspense>
      )}
    </AppLayout>
  );
};

export default ConsultarStock;
